import { useMemo } from 'react';
import DOMPurify from 'dompurify';
import { marked } from 'marked';
import { cn } from '@command-center/ui-system';

export function SafeMarkdown({
  markdown,
  className,
  emptyState = 'This document is empty.',
}: {
  markdown?: string | null;
  className?: string;
  emptyState?: string;
}) {
  const html = useMemo(() => {
    if (!markdown?.trim()) return null;
    const rendered = marked.parse(markdown, { async: false, breaks: true, gfm: true }) as string;
    return sanitizeHtml(rendered);
  }, [markdown]);

  if (!html) return <p className={cn('text-sm text-muted-foreground', className)}>{emptyState}</p>;

  return (
    <div
      className={cn('prose-notes break-words text-sm leading-relaxed', className)}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

export function SafeHtml({ html, className }: { html?: string | null; className?: string }) {
  const sanitized = useMemo(() => html?.trim() ? sanitizeHtml(html) : null, [html]);
  if (!sanitized) return null;
  return <div className={cn('break-words text-sm leading-relaxed', className)} dangerouslySetInnerHTML={{ __html: sanitized }} />;
}

export interface SanitizeHtmlOptions {
  allowFormPreview?: boolean;
  allowTaskInputs?: boolean;
}

export function sanitizeHtml(
  html: string,
  options: SanitizeHtmlOptions = {},
): string {
  const forbidTags = ['script', 'iframe', 'object', 'embed'];
  if (!options.allowFormPreview) forbidTags.push('form', 'button');
  if (!options.allowFormPreview && !options.allowTaskInputs) forbidTags.push('input');

  const sanitized = DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: forbidTags,
    FORBID_ATTR: ['style'],
  });
  if (typeof document === 'undefined') return sanitized;
  const template = document.createElement('template');
  template.innerHTML = sanitized;
  template.content.querySelectorAll('input').forEach((input) => {
    if (options.allowFormPreview) {
      input.setAttribute('disabled', '');
      input.removeAttribute('formaction');
      return;
    }
    if (options.allowTaskInputs && input.type === 'checkbox') {
      input.setAttribute('disabled', '');
      input.removeAttribute('name');
      input.removeAttribute('value');
      return;
    }
    input.remove();
  });
  template.content.querySelectorAll('button, select, textarea').forEach((control) => {
    if (options.allowFormPreview) {
      control.setAttribute('disabled', '');
      control.removeAttribute('formaction');
    }
  });
  template.content.querySelectorAll('form').forEach((form) => {
    form.removeAttribute('action');
    form.removeAttribute('method');
    form.setAttribute('inert', '');
  });
  template.content.querySelectorAll('a').forEach((link) => {
    const href = link.getAttribute('href') ?? '';
    if (!/^(https?:|mailto:|tel:|\/|#)/i.test(href)) {
      link.removeAttribute('href');
      return;
    }
    if (/^https?:/i.test(href)) {
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    } else {
      link.removeAttribute('target');
      link.removeAttribute('rel');
    }
  });
  return template.innerHTML;
}

export function sanitizeFormPreviewHtml(html?: string): string | undefined {
  if (!html?.trim()) return undefined;
  const sanitized = sanitizeHtml(html, { allowFormPreview: true });
  return sanitized.trim() || undefined;
}
